import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 11000-11999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const issuesErrors = {
  // Live at 3 sites: issues/issues.permission.ts (checkIssuesPermission, the shared gate consumed by
  // comments/fileSystemServer/reviews/pdfExport too) and comments/commentsMongo.service.ts. Confirmed
  // safe by issues/CLAUDE.md's own standing convention: "Server failures are rendered from errorCode,
  // not from errorMessage" — verified live at deletingIssues.actions.ts, IssueDetailsContent.tsx,
  // fileDownload.ts, all of which branch on this exact code with their own translated text.
  ISSUE_NOT_FOUND: {
    errorCode: 11001,
    errorMessage: t("Issue not found"),
    isDynamic: false,
  },
  // Live at 3 sites: issues.permission.ts + 2 in issues.mongo.service.ts (createIssue,
  // saveIssuesDataFromBCF). Same confirmed-safe convention as ISSUE_NOT_FOUND above.
  ISSUE_NO_ACCESS: {
    errorCode: 11002,
    errorMessage: t("You do not have Required permission"),
    isDynamic: false,
  },
  // Live at issues.mongo.service.ts's updateIssueCustomFields. The real message used to vary per
  // validation rule (validation.errorToastContent) — confirmed the frontend never read that specific
  // text either; IssueDetailsContent.tsx shows one fixed translated string for this code regardless
  // of which rule failed, so no dynamicParams are needed here despite the old per-site variance.
  ISSUE_CUSTOM_FIELDS_VALIDATION_FAILED: {
    errorCode: 11003,
    errorMessage: t("Fill all fields according to the rules before saving."),
    isDynamic: false,
  },
  // Live at issues.mongo.service.ts's updateIssueCustomFields (the Mongo write matched no document).
  // Confirmed safe — same standing convention as the other 3 issues codes above.
  ISSUE_CUSTOM_FIELDS_UPDATE_FAILED: {
    errorCode: 11004,
    errorMessage: t("Failed to update Custom Fields."),
    isDynamic: false,
  },
  // `attachedReviewId` on createIssue names a review the issue cannot join: it does not exist, or it
  // belongs to a different space. A review only ever holds resources from its own space.
  // Live at issues.mongo.service.ts's createIssue (attachedReviewId branch). No confirmed frontend
  // consumer found — grep across packages/web found no reference to this code.
  ISSUE_REVIEW_NOT_ATTACHABLE: {
    errorCode: 11005,
    errorMessage: t("The review does not exist"),
    isDynamic: false,
  },
  // Live at issues.mongo.service.ts's deleteIssuesData. Confirmed safe — deletingIssues.actions.ts's
  // deleteErrorContent branches on this exact code with its own translated text (same standing
  // convention as the other issues codes — see issues/CLAUDE.md).
  ISSUE_DELETE_BLOCKED_BY_REVIEW: {
    errorCode: 11006,
    errorMessage: t("Some Issue(s) could not be deleted as they are added to Reviews."),
    isDynamic: false,
  },
  // 11007-11009 is the issues module's SOMETHING_WENT_WRONG (9000) / RESOURCE_NOT_FOUND (9003)
  // split. The module needed almost no new codes: seven of its ten non-generic sites reused
  // ISSUE_NOT_FOUND (11001), SESSION_USER_NOT_FOUND (7079), PROJECT_NOT_FOUND, SPACE_NOT_FOUND
  // (7015) and FILE_NOT_FOUND (25025) verbatim. Nine sites stay generic, literally saying
  // "Something went wrong".
  // 11007-11009 — the issues module's split.
  ISSUE_CUSTOM_FIELD_NOT_FOUND_TO_DELETE: {
    errorCode: 11007,
    errorMessage: t("No matching custom field was found to delete."),
    isDynamic: false,
  },
  // Deliberately not FORM_NOT_FOUND, whose sentence is "Form not found".
  ISSUE_FORM_DOES_NOT_EXIST: {
    errorCode: 11008,
    errorMessage: t("Form does not exists"),
    isDynamic: false,
  },
  ISSUE_FORM_NO_ACTIONS_ALLOWED: {
    errorCode: 11009,
    errorMessage: t("No action can be performed in this form"),
    isDynamic: false,
  },
  // 11010-11013 came off NO_CHANGES_APPLIED (9005).
  ISSUE_CUSTOM_FIELD_ALREADY_EXISTS: {
    errorCode: 11010,
    errorMessage: t("Custom Field Already Exists"),
    isDynamic: false,
  },
  ISSUE_CUSTOM_FIELD_UNCHANGED: {
    errorCode: 11011,
    errorMessage: t("No custom field was updated — the id did not match, or the values were unchanged."),
    isDynamic: false,
  },
  ISSUE_FORM_UNLINK_MATCHED_NOTHING: {
    errorCode: 11012,
    errorMessage: t("No Issue was unlinked — the ids matched nothing, or none of them carried this form."),
    isDynamic: false,
  },
  ISSUE_FORM_LINK_MATCHED_NOTHING: {
    errorCode: 11013,
    errorMessage: t("No Issue was linked — the ids matched nothing, or every one already carried this form."),
    isDynamic: false,
  },
  // 11014 (ISSUE_IDS_MATCHED_NO_ISSUE) is retired: nothing sends it. Do not reuse the number.
  // `handleIssuesExportAsPDF` (web issues/state/bcfData.actions.ts) resolved the project's issue ids
  // and found none — the export is refused before any request is made, so this code is minted on the
  // client rather than forwarded from the server.
  ISSUES_NO_ISSUES_TO_EXPORT: {
    errorCode: 11015,
    errorMessage: t("There are no issues to export."),
    isDynamic: false,
  },
  ISSUE_DUE_DATE_IN_PAST: {
    errorCode: 11016,
    errorMessage: t("Due Date cannot be in the past"),
    isDynamic: false,
  },
  // 11017-11019: createIssue's `reviewErrorCode`, sent on a SUCCESS: the issue exists but is not in its attachedReviewId review.
  ISSUE_CREATED_REVIEW_CLOSED: {
    errorCode: 11017,
    errorMessage: t("The issue was created, but not added to the review because the review is closed."),
    isDynamic: false,
  },
  ISSUE_CREATED_REVIEW_REVOKED: {
    errorCode: 11018,
    errorMessage: t("The issue was created, but not added to the review because the review has been revoked."),
    isDynamic: false,
  },
  ISSUE_CREATED_NOT_ADDED_TO_REVIEW: {
    errorCode: 11019,
    errorMessage: t("The issue was created, but could not be added to the review."),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
